/**
 * Horizontal quota meter (plan usage). A sequential blue fill over a neutral
 * track shows used / limit with the fraction and percent as direct labels
 * (e.g. public views vs the plan's yearly allowance). When unlimited (limit
 * === null) it shows a
 * calm "Unlimited" state — full neutral track, no alarming fill.
 */

export interface QuotaMeterProps {
  used: number;
  /** null = unlimited plan. */
  limit: number | null;
  label: string;
  unlimitedLabel: string;
  formatNumber: (n: number) => string;
}

export function QuotaMeter({ used, limit, label, unlimitedLabel, formatNumber }: QuotaMeterProps) {
  const unlimited = limit === null;
  const pct = unlimited || limit === 0 ? 0 : Math.min(Math.round((used / limit) * 100), 100);
  const fillWidth = unlimited || limit === 0 ? 0 : Math.min((used / limit) * 100, 100);

  return (
    <div className="dpp-quota">
      <div className="dpp-quota__head">
        <span className="dpp-quota__label">{label}</span>
        <span className="dpp-quota__value">
          {unlimited
            ? `${formatNumber(used)} · ${unlimitedLabel}`
            : `${formatNumber(used)} / ${formatNumber(limit)}`}
        </span>
      </div>
      <div
        className="dpp-quota__track"
        role="progressbar"
        aria-valuenow={unlimited ? undefined : used}
        aria-valuemin={0}
        aria-valuemax={unlimited ? undefined : limit}
        aria-label={label}
      >
        {!unlimited && <div className="dpp-quota__fill" style={{ width: `${fillWidth}%` }} />}
      </div>
      <span className="dpp-quota__foot">{unlimited ? unlimitedLabel : `${pct}%`}</span>
    </div>
  );
}
